import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useStore,
  useTranslate,
  useUpdate,
} from 'react-admin'
import ToneChip from '../components/ToneChip'
import { getAlertSeverityColor } from '../types/alerts'
import { GLUCOSE_THRESHOLDS } from '../types/dashboard'
import { MMOL_TO_MGDL_FACTOR, mgdlToMmol, mmolToMgdl } from '../utils/glucoseUnits'
import { AlertThresholdCopyBanner, CopiedRule } from './AlertThresholdCopyBanner'
import {
  enqueueRulesSave,
  isRulesSaveRunning,
  trackRulesWrite,
  whenRulesSavesIdle,
} from './alertRulesSaveQueue'

export interface AlertRule {
  id?: number
  pet: number | { id: number }
  ruleType:
    | 'critical_hypoglycemia'
    | 'warning_hypoglycemia'
    | 'critical_hyperglycemia'
    | 'warning_hyperglycemia'
    | 'rapid_drop'
    | 'dawn_pattern'
    | 'somogyi_pattern'
    | 'postprandial_spikes'
  isActive: boolean

  // Glucose Thresholds (conditionally required based on ruleType)
  criticalThreshold?: number
  warningThreshold?: number
  hyperglycemiaThreshold?: number
  severeHyperglycemiaThreshold?: number
  somogyiReboundThreshold?: number
  dawnElevationThreshold?: number
  rapidDropThreshold?: number
  postprandialSpikeThreshold?: number
  severeSpikeThreshold?: number
  postprandialWindowHours?: number
  preMealWindowHours?: number

  // Pattern Analysis
  patternAnalysisWindow?: number // Time window for pattern detection (hours)
  thresholdUnit: 'mmol/L' | 'mg/dL'

  // Notifications
  emailNotificationEnabled?: boolean
  smsNotificationEnabled?: boolean
  // notificationRecipients was removed: vet recipients are derived server-side
  // from the pet owner's `vets` relationship by the Go alert-engine.

  // Metadata
  lastTriggered?: Date
  triggerCount: number
  createdBy?: number
  notes?: string
  createdAt?: string
  updatedAt?: string
}

// UI scope: the Pet Edit "Glucose Alert Rules" screen is restricted to the
// two low-glucose cases. This is a UI choice, NOT a backend constraint.
//
// The Go alert-engine (go-services/internal/alert/processor.go) actively
// processes a wider set — critical/warning hyperglycemia, rapid_drop,
// postprandial_spike, pattern_detected, sensor_failure — and ada-core's
// AlertRules collection (src/collections/AlertRules/index.ts) accepts all
// ten ruleType values. So:
//   - A rule of an unsupported type that exists in the DB (older data, an
//     admin-panel create, etc.) DOES fire alerts via the engine — it's just
//     not editable from this screen. Filtered out below before render.
//   - When you add a new ruleType to ada-core's enum, decide here whether
//     to also expose it in this UI (add to ALLOWED_RULE_TYPES + extend the
//     render configs). Otherwise it'll silently exist but be invisible.
const ALLOWED_RULE_TYPES = ['critical_hypoglycemia', 'warning_hypoglycemia'] as const

// Rule type definitions for organized display
const ruleTypeDefinitions = {
  // Basic Glucose Monitoring (Essential)
  basic: [...ALLOWED_RULE_TYPES] as string[],
  // Advanced Pattern Detection (Optional) — none supported currently
  advanced: [] as string[],
}

const getRuleTypeName = (ruleType: string): string => {
  const ruleTypeLabels: Record<string, string> = {
    critical_hypoglycemia: 'Critical Hypoglycemia Alert',
    warning_hypoglycemia: 'Warning Hypoglycemia Alert',
    critical_hyperglycemia: 'Critical Hyperglycemia Alert',
    warning_hyperglycemia: 'Warning Hyperglycemia Alert',
    rapid_drop: 'Rapid Glucose Drop Alert',
    dawn_pattern: 'Dawn Phenomenon Detection',
    somogyi_pattern: 'Somogyi Phenomenon Detection',
    postprandial_spikes: 'Postprandial Spike Alert',
  }
  return ruleTypeLabels[ruleType] || `${ruleType} Alert`
}

const getDefaultRuleConfig = (ruleType: string, petId: number): AlertRule => {
  const baseConfig = {
    pet: petId,
    ruleType: ruleType as any,
    thresholdUnit: 'mmol/L' as const,
    isActive: true,
    triggerCount: 0,
    emailNotificationEnabled: true,
    smsNotificationEnabled: false,
  }

  switch (ruleType) {
    case 'critical_hypoglycemia':
      return {
        ...baseConfig,
        criticalThreshold: 2.8,
        notes: 'Critical low glucose requiring immediate intervention',
      }
    case 'warning_hypoglycemia':
      return {
        ...baseConfig,
        warningThreshold: 4.0,
        notes: 'Warning level low glucose requiring monitoring',
      }
    case 'critical_hyperglycemia':
      return {
        ...baseConfig,
        severeHyperglycemiaThreshold: 16.0,
        notes: 'Critical high glucose requiring immediate vet intervention',
      }
    case 'warning_hyperglycemia':
      return {
        ...baseConfig,
        hyperglycemiaThreshold: 12.0,
        severeHyperglycemiaThreshold: 16.0,
        notes: 'High glucose requiring monitoring',
      }
    case 'rapid_drop':
      return {
        ...baseConfig,
        rapidDropThreshold: 2.0,
        warningThreshold: 4.0,
        notes: 'Rapid glucose drop requiring monitoring',
      }
    case 'dawn_pattern':
      return {
        ...baseConfig,
        dawnElevationThreshold: 9.0,
        patternAnalysisWindow: 24,
        notes: 'Morning glucose elevation pattern detection',
      }
    case 'somogyi_pattern':
      return {
        ...baseConfig,
        somogyiReboundThreshold: 10.0,
        patternAnalysisWindow: 24,
        notes: 'Rebound hyperglycemia after hypoglycemic episodes',
      }
    case 'postprandial_spikes':
      return {
        ...baseConfig,
        postprandialSpikeThreshold: 6.0,
        severeSpikeThreshold: 9.0,
        postprandialWindowHours: 4.0,
        preMealWindowHours: 1.0,
        notes: 'Post-meal glucose spike detection',
      }
    default:
      return { ...baseConfig, notes: 'Custom alert rule' }
  }
}

const getRuleTypeColor = (ruleType: string) => {
  switch (ruleType) {
    case 'critical_hypoglycemia':
    case 'critical_hyperglycemia':
      return getAlertSeverityColor('CRITICAL')
    case 'warning_hypoglycemia':
    case 'warning_hyperglycemia':
      return getAlertSeverityColor('HIGH')
    case 'rapid_drop':
      return '#9c27b0'
    case 'dawn_pattern':
      return '#2196f3'
    case 'somogyi_pattern':
      return '#ff9800'
    case 'postprandial_spikes':
      return '#4caf50'
    default:
      return getAlertSeverityColor('default')
  }
}

const getRuleTypeIcon = (ruleType: string) => {
  switch (ruleType) {
    case 'critical_hypoglycemia':
    case 'critical_hyperglycemia':
      return <ErrorIcon style={{ color: getAlertSeverityColor('CRITICAL') }} />
    case 'warning_hypoglycemia':
    case 'warning_hyperglycemia':
      return <WarningIcon style={{ color: getAlertSeverityColor('HIGH') }} />
    case 'rapid_drop':
      return <WarningIcon style={{ color: '#9c27b0' }} />
    case 'dawn_pattern':
      return <InfoIcon style={{ color: '#2196f3' }} />
    case 'somogyi_pattern':
      return <WarningIcon style={{ color: '#ff9800' }} />
    case 'postprandial_spikes':
      return <InfoIcon style={{ color: '#4caf50' }} />
    default:
      return <InfoIcon />
  }
}

const getRuleTypeLabel = (ruleType: string, translate: any) => {
  const labels: Record<string, string> = {
    critical_hypoglycemia: translate('pos.alert_rules.rule_types.critical_hypoglycemia'),
    warning_hypoglycemia: translate('pos.alert_rules.rule_types.warning_hypoglycemia'),
    critical_hyperglycemia: translate('pos.alert_rules.rule_types.critical_hyperglycemia'),
    warning_hyperglycemia: translate('pos.alert_rules.rule_types.warning_hyperglycemia'),
    rapid_drop: translate('pos.alert_rules.rule_types.rapid_drop'),
    dawn_pattern: translate('pos.alert_rules.rule_types.dawn_pattern'),
    somogyi_pattern: translate('pos.alert_rules.rule_types.somogyi_pattern'),
    postprandial_spikes: translate('pos.alert_rules.rule_types.postprandial_spikes'),
  }
  return labels[ruleType] || ruleType
}

// Dynamic field visibility based on rule type
interface FieldVisibility {
  criticalThreshold: boolean
  warningThreshold: boolean
  hyperglycemiaThreshold: boolean
  severeHyperglycemiaThreshold: boolean
  somogyiReboundThreshold: boolean
  dawnElevationThreshold: boolean
  rapidDropThreshold: boolean
  patternAnalysisWindow: boolean
  postprandialSpikeThreshold: boolean
  severeSpikeThreshold: boolean
  postprandialWindowHours: boolean
  preMealWindowHours: boolean
}

const getFieldVisibility = (ruleType: string): FieldVisibility => {
  const visibility: FieldVisibility = {
    criticalThreshold: false,
    warningThreshold: false,
    hyperglycemiaThreshold: false,
    severeHyperglycemiaThreshold: false,
    somogyiReboundThreshold: false,
    dawnElevationThreshold: false,
    rapidDropThreshold: false,
    patternAnalysisWindow: false,
    postprandialSpikeThreshold: false,
    severeSpikeThreshold: false,
    postprandialWindowHours: false,
    preMealWindowHours: false,
  }

  switch (ruleType) {
    case 'critical_hypoglycemia':
      visibility.criticalThreshold = true
      break

    case 'warning_hypoglycemia':
      visibility.warningThreshold = true
      break

    case 'critical_hyperglycemia':
      visibility.severeHyperglycemiaThreshold = true
      break

    case 'warning_hyperglycemia':
      visibility.hyperglycemiaThreshold = true
      visibility.severeHyperglycemiaThreshold = true
      break

    case 'rapid_drop':
      visibility.rapidDropThreshold = true
      visibility.warningThreshold = true
      break

    case 'somogyi_pattern':
      visibility.somogyiReboundThreshold = true
      visibility.patternAnalysisWindow = true
      break

    case 'dawn_pattern':
      visibility.dawnElevationThreshold = true
      visibility.patternAnalysisWindow = true
      break

    case 'postprandial_spikes':
      visibility.postprandialSpikeThreshold = true
      visibility.severeSpikeThreshold = true
      visibility.postprandialWindowHours = true
      visibility.preMealWindowHours = true
      break
  }

  return visibility
}

// Helper function to convert glucose range text from mmol/L to mg/dL
const convertRangeText = (text: string, targetUnit: 'mmol/L' | 'mg/dL'): string => {
  if (targetUnit === 'mmol/L') return text

  // Convert all mmol/L values to mg/dL in the text
  return text.replace(/(\d+\.?\d*)\s*-?\s*(\d+\.?\d*)?\s*mmol\/L/g, (match, num1, num2) => {
    const val1 = mmolToMgdl(parseFloat(num1)).toString()
    if (num2) {
      const val2 = mmolToMgdl(parseFloat(num2)).toString()
      return `${val1}-${val2} mg/dL`
    }
    return `${val1} mg/dL`
  })
}

// Every numeric glucose-threshold field on a rule. Used to convert a rule's
// thresholds between units. `thresholdUnit` tags the unit the in-memory values
// are currently expressed in (deltas like rebound/spike convert by the same
// multiplicative factor as absolute readings).
const THRESHOLD_FIELDS = [
  'criticalThreshold',
  'warningThreshold',
  'hyperglycemiaThreshold',
  'severeHyperglycemiaThreshold',
  'somogyiReboundThreshold',
  'dawnElevationThreshold',
  'rapidDropThreshold',
  'postprandialSpikeThreshold',
  'severeSpikeThreshold',
] as const

/**
 * Convert each rule's threshold values from its own `thresholdUnit` to
 * `targetUnit`, returning rules tagged with the target unit (rules already in
 * the target unit are returned unchanged).
 *
 * Thresholds are PERSISTED canonically in mmol/L (the unit the Go alert engine
 * computes in). This helper is the boundary converter:
 *  - load: stored mmol/L → the app-bar display unit (for editing),
 *  - banner change: re-express the in-memory values in the new display unit,
 *  - save: display unit → mmol/L before persisting.
 * The app-bar switcher is the single place to choose the display unit.
 */
export const convertRulesToUnit = (
  rules: AlertRule[],
  targetUnit: 'mmol/L' | 'mg/dL',
): AlertRule[] =>
  rules.map((rule) => {
    const from = (rule.thresholdUnit as 'mmol/L' | 'mg/dL') || 'mmol/L'
    if (from === targetUnit) return rule
    const factor = targetUnit === 'mg/dL' ? MMOL_TO_MGDL_FACTOR : 1 / MMOL_TO_MGDL_FACTOR
    const converted = { ...rule, thresholdUnit: targetUnit } as AlertRule
    const ruleRecord = rule as unknown as Record<string, unknown>
    const convertedRecord = converted as unknown as Record<string, unknown>
    for (const field of THRESHOLD_FIELDS) {
      const value = ruleRecord[field]
      if (typeof value === 'number') {
        convertedRecord[field] = parseFloat((value * factor).toFixed(1))
      }
    }
    return converted
  })

const AlertRulesEditor: React.FC = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const translate = useTranslate()
  const [update] = useUpdate()
  const { identity } = useGetIdentity()
  const petId = record?.id ? Number(record.id) : undefined

  const [alertRules, setAlertRules] = useState<AlertRule[]>([])
  const [loading, setLoading] = useState(false)
  // X3 (TLA+ docs/spec/tla/AlertRulesPets.tla): the load FAILED. The editor then
  // shows an error + retry, never editable defaults -- see the render below.
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [autoSaving, setAutoSaving] = useState(false)
  // Display unit is the app-wide one set in the top app bar — the single place
  // to choose units. This editor only reads it and converts thresholds to match;
  // it no longer has its own unit toggle.
  const [globalUnit] = useStore<'mmol/L' | 'mg/dL'>('glucoseUnit', 'mmol/L')
  const [hasChanges, setHasChanges] = useState(false)
  // Autosave concurrency (TLA+ AlertRulesAutosave + AlertRulesCoordinator). Only
  // the Save BUTTON is disabled while a save runs -- the inputs stay live -- so
  // an edit can land mid-save. `editRev` counts edits; `snapRev` is the revision
  // the last save job snapshotted, so a job with nothing newer does nothing
  // (leaving a pet mid-save must not save it twice); -1 forces the next attempt
  // after a failure, so the vet can retry the same values. Saves themselves are
  // serialized PER PET by alertRulesSaveQueue, which outlives this editor.
  const editRev = useRef(0)
  const snapRev = useRef(0)
  const mountedRef = useRef(true)
  const loadedRef = useRef(false)
  const [waitingForQueue, setWaitingForQueue] = useState(false)
  const hasChangesRef = useRef(hasChanges)
  hasChangesRef.current = hasChanges
  const loadFailedRef = useRef(loadFailed)
  loadFailedRef.current = loadFailed
  // The editor's rules NOW -- after an unmount, its last rules, which a queued
  // save of this editor still snapshots (the vet may remove one while it runs).
  const rulesRef = useRef(alertRules)
  rulesRef.current = alertRules
  const unitRef = useRef(globalUnit)
  unitRef.current = globalUnit
  // Types whose card was removed while a save ran; that save may be creating
  // one of them right now, with no id the remove could have deleted.
  const removedDuringSave = useRef(new Set<string>())
  // A card on screen is the rule a save sent iff the type matches AND it still
  // carries the id the save started with (both absent for a new rule). A card
  // removed and re-added mid-save has the same type but no id: a DIFFERENT rule.
  const sameRule = (onScreen: AlertRule, sent: { ruleType: string; id?: AlertRule['id'] }) =>
    onScreen.ruleType === sent.ruleType && (onScreen.id ?? null) === (sent.id ?? null)
  // The module's ONE server delete (pinned by alertRulesNoDeadWrites.test.tsx,
  // VP-ALERTS-R14): always a single rule, by that rule's own id -- never a
  // broadcast, never a rule the editor did not load or create. Callers: the
  // remove button, and a save whose freshly created rule the vet removed
  // before it had an id (R36).
  const deleteRuleOnServer = (id: AlertRule['id']) => dataProvider.delete('alert-rules', { id })
  const markEdited = () => {
    editRev.current += 1
    setHasChanges(true)
  }
  const [calculatedGlucoseRange, setCalculatedGlucoseRange] = useState<string>('')
  const [addRuleMenuAnchor, setAddRuleMenuAnchor] = useState<null | HTMLElement>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>(
    {},
  )
  // True once we've loaded the pet and found it already has persisted rules.
  // Gates the "copy thresholds from a previous profile" banner so it only
  // appears for freshly-created pets that would otherwise start from defaults.
  const [hadSavedRules, setHadSavedRules] = useState(false)

  // Validation ranges for each threshold field (in mmol/L)
  const validationRangesMmol: Record<string, { min: number; max: number }> = {
    criticalThreshold: { min: 1.0, max: 4.0 },
    warningThreshold: { min: 3.0, max: 5.5 },
    hyperglycemiaThreshold: { min: 8.0, max: 25.0 },
    severeHyperglycemiaThreshold: { min: 12.0, max: 30.0 },
    somogyiReboundThreshold: { min: 6.0, max: 20.0 },
    dawnElevationThreshold: { min: 5.0, max: 15.0 },
    rapidDropThreshold: { min: 1.0, max: 5.0 },
    postprandialSpikeThreshold: { min: 3.0, max: 10.0 },
    severeSpikeThreshold: { min: 5.0, max: 15.0 },
    postprandialWindowHours: { min: 2.0, max: 6.0 },
    preMealWindowHours: { min: 0.5, max: 3.0 },
    patternAnalysisWindow: { min: 12, max: 72 },
  }

  // Helper function to get validation range in current unit
  const getInputPropsForField = (fieldName: string): { step: number; min: number; max: number } => {
    const range = validationRangesMmol[fieldName]
    if (!range) {
      return { step: 0.1, min: 0, max: 100 }
    }

    // Non-glucose fields (hours) don't need unit conversion
    if (
      fieldName === 'patternAnalysisWindow' ||
      fieldName === 'postprandialWindowHours' ||
      fieldName === 'preMealWindowHours'
    ) {
      const step = fieldName === 'patternAnalysisWindow' ? 1 : 0.5
      return { step, min: range.min, max: range.max }
    }

    // Convert to mg/dL if needed
    if (globalUnit === 'mg/dL') {
      return {
        step: 1,
        min: mmolToMgdl(range.min),
        max: mmolToMgdl(range.max),
      }
    }

    return { step: 0.1, min: range.min, max: range.max }
  }

  // Validate a single field
  const validateField = (
    ruleType: string,
    fieldName: string,
    value: number | undefined,
    unit: 'mmol/L' | 'mg/dL' = globalUnit,
  ): string | null => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return null // Don't show error for empty fields
    }

    const range = validationRangesMmol[fieldName]
    if (!range) {
      return null // No validation for this field
    }

    // For non-glucose fields (hours), validate directly without conversion
    if (
      fieldName === 'patternAnalysisWindow' ||
      fieldName === 'postprandialWindowHours' ||
      fieldName === 'preMealWindowHours'
    ) {
      // Use small epsilon for floating-point comparison (inclusive boundaries)
      const epsilon = 0.001
      if (value < range.min - epsilon || value > range.max + epsilon) {
        return translate(`pos.alert_rules.validation.${fieldName}`)
      }
      return null
    }

    // For glucose fields, validate in the current display unit to avoid
    // floating-point precision issues when converting boundary values.
    // The display range uses rounded values, so we should validate against those.
    if (unit === 'mg/dL') {
      // Get the rounded display range in mg/dL (same as shown in UI)
      const minMgdL = mmolToMgdl(range.min)
      const maxMgdL = mmolToMgdl(range.max)
      // Use inclusive comparison for boundary values
      if (value < minMgdL || value > maxMgdL) {
        return translate(`pos.alert_rules.validation.${fieldName}`)
      }
    } else {
      // For mmol/L, use small epsilon for floating-point comparison (inclusive boundaries)
      const epsilon = 0.001
      if (value < range.min - epsilon || value > range.max + epsilon) {
        return translate(`pos.alert_rules.validation.${fieldName}`)
      }
    }

    return null
  }

  // Validate all rules and update validation errors
  // `silent` validates without touching this editor's error display (a queued
  // save of an editor that is gone). Each rule is checked in its own unit.
  const validateAllRules = (rules: AlertRule[], silent = false): boolean => {
    const errors: Record<string, Record<string, string>> = {}
    let hasErrors = false

    rules.forEach((rule, index) => {
      const ruleErrors: Record<string, string> = {}
      const visibility = getFieldVisibility(rule.ruleType)

      // Validate each visible field
      Object.keys(visibility).forEach((field) => {
        if (visibility[field as keyof FieldVisibility]) {
          // Each rule in ITS OWN unit: rules can be mixed (#36 review).
          const unit = (rule.thresholdUnit as 'mmol/L' | 'mg/dL') || globalUnit
          const error = validateField(rule.ruleType, field, (rule as any)[field], unit)
          if (error) {
            ruleErrors[field] = error
            hasErrors = true
          }
        }
      })

      if (Object.keys(ruleErrors).length > 0) {
        errors[`${rule.ruleType}-${index}`] = ruleErrors
      }
    })

    if (!silent) setValidationErrors(errors)
    return !hasErrors
  }

  // Function to calculate and update glucose range from hypoglycemia and hyperglycemia thresholds
  // `pets.glucoserange` write machinery DELETED (2026-07-28, eng-brain
  // P23-2/P27-1): the field is write-only — vetpo never reads it back,
  // AdapetApp derives its range from thresholds and only copies the field
  // during sync, ada-core and the Go engine pass it through. What lived here:
  // three failsafe write paths (react-admin update -> four guessed endpoint
  // spellings -> localStorage), a stale-localStorage replay with an unused
  // timestamp, and two sessionStorage-token bails (L-019 class). Existing DB
  // values simply go stale; the on-screen range PREVIEW below is computed
  // display-only state and is unaffected. The logout sweep still clears the
  // legacy glucoseRange_<petId> keys old browsers may carry (storeConfig.ts).

  // Fetch existing alert rules for this pet
  useEffect(() => {
    if (!petId) return

    // A response for a pet that is no longer on screen must not touch this
    // editor (PetEdit keeps the instance across /pets/A -> /pets/B): a late
    // failure used to flip the NEW pet to "could not load", and a late success
    // to show the OLD pet's rules under the new one.
    let cancelled = false
    const fetchAlertRules = async () => {
      setLoading(true)
      // A new pet starts clean. Without this the previous pet's hasChanges
      // carried over: its armed autosave then saved THIS pet's rules -- after
      // a failed load, the seeded DEFAULTS, which the create-conflict recovery
      // writes over the pet's tuned thresholds (the L-033 reset). Only a
      // successful load may mark the editor dirty (below).
      setHasChanges(false)
      setLoadFailed(false)
      // #36: this pet's queue may still hold the PREVIOUS editor's save (or its
      // flush on unmount). Loading before it lands showed the old values, clean,
      // and the next edit then re-saved them over the new ones (X1/X2).
      setWaitingForQueue(true)
      await whenRulesSavesIdle(petId)
      if (cancelled) return
      setWaitingForQueue(false)

      try {
        // Try multiple endpoints for alert rules
        const endpoints = ['alert-rules', 'alertRules', 'alert_rules']

        let rules: AlertRule[] = []
        let success = false

        for (const endpoint of endpoints) {
          try {
            const response = await dataProvider.getList(endpoint, {
              pagination: { page: 1, perPage: 100 },
              sort: { field: 'id', order: 'ASC' },
              filter: { pet: petId },
            })

            if (response.data) {
              rules = response.data.map((rule: any) => ({
                id: rule.id,
                pet: typeof rule.pet === 'object' ? rule.pet.id : rule.pet || rule.pet_id || petId,
                ruleType: rule.ruleType || rule.rule_type || rule.type,
                isActive:
                  rule.isActive !== undefined
                    ? rule.isActive
                    : rule.is_active !== undefined
                      ? rule.is_active
                      : true,

                // Glucose Thresholds
                criticalThreshold: rule.criticalThreshold || rule.critical_threshold,
                warningThreshold: rule.warningThreshold || rule.warning_threshold,
                hyperglycemiaThreshold: rule.hyperglycemiaThreshold || rule.hyperglycemia_threshold,
                severeHyperglycemiaThreshold:
                  rule.severeHyperglycemiaThreshold || rule.severe_hyperglycemia_threshold,
                rapidDropThreshold: rule.rapidDropThreshold || rule.rapid_drop_threshold,
                somogyiReboundThreshold:
                  rule.somogyiReboundThreshold || rule.somogyi_rebound_threshold,
                dawnElevationThreshold:
                  rule.dawnElevationThreshold || rule.dawn_elevation_threshold,
                postprandialSpikeThreshold:
                  rule.postprandialSpikeThreshold || rule.postprandial_spike_threshold,
                severeSpikeThreshold: rule.severeSpikeThreshold || rule.severe_spike_threshold,
                postprandialWindowHours:
                  rule.postprandialWindowHours || rule.postprandial_window_hours,
                preMealWindowHours: rule.preMealWindowHours || rule.pre_meal_window_hours,

                // Pattern Analysis
                patternAnalysisWindow:
                  rule.patternAnalysisWindow || rule.pattern_analysis_window || 24,
                thresholdUnit: rule.thresholdUnit || rule.threshold_unit || 'mmol/L',

                // Notifications
                emailNotificationEnabled:
                  rule.emailNotificationEnabled !== undefined
                    ? rule.emailNotificationEnabled
                    : rule.email_notification_enabled,
                smsNotificationEnabled:
                  rule.smsNotificationEnabled !== undefined
                    ? rule.smsNotificationEnabled
                    : rule.sms_notification_enabled,

                // Metadata
                lastTriggered:
                  rule.lastTriggered || rule.last_triggered
                    ? new Date(rule.lastTriggered || rule.last_triggered)
                    : undefined,
                triggerCount: rule.triggerCount || rule.trigger_count || 0,
                createdBy: rule.createdBy || rule.created_by,
                notes: rule.notes || rule.description || '',
              }))
              success = true
              break
            }
          } catch (error) {
            console.warn(`Alert rules endpoint ${endpoint} failed:`, error)
          }
        }

        // Record whether the pet already had persisted rules BEFORE we seed
        // any defaults — this drives the "copy from a previous profile" banner.
        if (cancelled) return
        setHadSavedRules(success && rules.length > 0)

        if (!success || rules.length === 0) {
          // Initialize with basic monitoring rules if none exist
          rules = ruleTypeDefinitions.basic.map((ruleType) =>
            getDefaultRuleConfig(ruleType, petId!),
          )
          // ⚠️ Seed for DISPLAY always; mark dirty (which the debounced effect
          // below turns into a WRITE) only when the load SUCCEEDED, i.e. when we
          // actually know this pet has no rules. If `success` is false every
          // endpoint threw and we know NOTHING — persisting defaults then
          // overwrites the vet's tuned thresholds via the create-conflict
          // recovery path, which is how "alert thresholds reset themselves"
          // happens. Load failure must never be read as "no rules exist".
          // See eng-brain L-033 + alertRulesLoadFailure.test.tsx.
          if (success) {
            markEdited() // Mark as having changes to save defaults
          } else {
            // X3: every endpoint failed. Showing these defaults as the pet's
            // rules invited an edit, and ONE edit saved them all: the creates
            // conflicted and the recovery path UPDATED the pet's tuned rules
            // with the defaults -- the L-033 reset reached through the vet.
            setLoadFailed(true)
          }
        }

        // Ensure no duplicate rule types and normalize rules
        const rulesByType = new Map<string, AlertRule>()
        for (const rule of rules) {
          if (!rulesByType.has(rule.ruleType)) {
            rulesByType.set(rule.ruleType, rule)
          }
        }
        rules = Array.from(rulesByType.values())

        // Drop any rule whose ruleType this UI doesn't surface (see
        // ALLOWED_RULE_TYPES at the top of this file — UI scope, not
        // backend scope). Filtered rules still fire via the alert-engine;
        // they're just not editable from this screen.
        rules = rules.filter((rule) =>
          (ALLOWED_RULE_TYPES as readonly string[]).includes(rule.ruleType),
        )

        // If filtering left no supported rules (e.g. an older pet that only had
        // hyperglycemia/pattern rules), seed the low-glucose defaults so the vet
        // always sees the two supported alerts.
        if (rules.length === 0) {
          rules = ruleTypeDefinitions.basic.map((ruleType) =>
            getDefaultRuleConfig(ruleType, petId!),
          )
          markEdited()
        }

        // Express loaded thresholds in the app-wide display unit (rules are
        // stored paired with their own thresholdUnit, which may differ).
        rules = convertRulesToUnit(rules, globalUnit)

        setAlertRules(rules)
        loadedRef.current = true

        // Update glucose range after loading rules
        if (rules.length > 0) {
          // Preview is display-only, so it is safe on any path.
          setCalculatedGlucoseRange(calculateGlucoseRangePreview(rules))
        }
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching alert rules:', error)
        notify(translate('pos.alert_rules.notifications.could_not_load'), { type: 'warning' })

        // Use defaults on error. Defaults are authored in mmol/L; express them
        // in the active display unit like the loaded-rules path does.
        const defaultRulesWithPet = convertRulesToUnit(
          ruleTypeDefinitions.basic.map((ruleType) => getDefaultRuleConfig(ruleType, petId!)),
          globalUnit,
        )
        setAlertRules(defaultRulesWithPet)
        setCalculatedGlucoseRange(calculateGlucoseRangePreview(defaultRulesWithPet))
        // Deliberately NOT setHasChanges(true): this is the load-FAILED path, so
        // these defaults are a display fallback only. Persisting them would
        // overwrite whatever the pet really has. See L-033. X3: not even
        // displayed as editable.
        setLoadFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAlertRules()
    return () => {
      cancelled = true
    }
  }, [petId, dataProvider, notify, reloadKey])

  // Calculate glucose range without saving (for real-time preview)
  const calculateGlucoseRangePreview = (rules: AlertRule[]) => {
    const criticalHypo = rules.find((r) => r.ruleType === 'critical_hypoglycemia' && r.isActive)
    const warningHypo = rules.find((r) => r.ruleType === 'warning_hypoglycemia' && r.isActive)
    const warningHyper = rules.find((r) => r.ruleType === 'warning_hyperglycemia' && r.isActive)

    // Defaults from the app-standard target range (GLUCOSE_THRESHOLDS —
    // the literal 3.9 was stale here after LOW was raised to 4.4)
    let lowThreshold: number = GLUCOSE_THRESHOLDS.LOW
    let highThreshold: number = GLUCOSE_THRESHOLDS.TARGET_MAX

    // Use the higher of critical or warning hypoglycemia thresholds as the low end
    if (criticalHypo?.criticalThreshold) {
      lowThreshold = criticalHypo.criticalThreshold
    } else if (warningHypo?.warningThreshold) {
      lowThreshold = warningHypo.warningThreshold
    }

    // Use warning hyperglycemia threshold as the high end
    if (warningHyper?.hyperglycemiaThreshold) {
      highThreshold = warningHyper.hyperglycemiaThreshold
    }

    // Convert to mmol/L if needed
    if (criticalHypo?.thresholdUnit === 'mg/dL' && criticalHypo.criticalThreshold) {
      lowThreshold = mgdlToMmol(criticalHypo.criticalThreshold)
    } else if (warningHypo?.thresholdUnit === 'mg/dL' && warningHypo.warningThreshold) {
      lowThreshold = mgdlToMmol(warningHypo.warningThreshold)
    }

    if (warningHyper?.thresholdUnit === 'mg/dL' && warningHyper.hyperglycemiaThreshold) {
      highThreshold = mgdlToMmol(warningHyper.hyperglycemiaThreshold)
    }

    return `${lowThreshold.toFixed(1)} - ${highThreshold.toFixed(1)}`
  }

  const handleRuleChange = (index: number, field: keyof AlertRule, value: any) => {
    const updatedRules = [...alertRules]
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value,
    }
    setAlertRules(updatedRules)
    markEdited()

    // Update calculated glucose range preview
    setCalculatedGlucoseRange(calculateGlucoseRangePreview(updatedRules))

    // Validate the updated rules
    validateAllRules(updatedRules)
  }

  const handleAddRule = (ruleType: string) => {
    // Check if rule type already exists
    const existingRule = alertRules.find((rule) => rule.ruleType === ruleType)
    if (existingRule) {
      notify('pos.alert_rules.toast.already_exists', {
        type: 'warning',
        messageArgs: { rule: getRuleTypeLabel(ruleType, translate) },
      })
      return
    }

    // Defaults are mmol/L; express the new card in the display unit, or it sits
    // among mg/dL rules as a bare "2.8" under a "(mg/dL)" label (#36 review).
    const [newRule] = convertRulesToUnit([getDefaultRuleConfig(ruleType, petId!)], globalUnit)
    const updatedRules = [...alertRules, newRule]
    setAlertRules(updatedRules)
    markEdited()

    // Update calculated glucose range preview
    setCalculatedGlucoseRange(calculateGlucoseRangePreview(updatedRules))

    // Validate the updated rules to clear any stale validation errors
    // This ensures the Save button is enabled if the new rule is valid
    validateAllRules(updatedRules)
  }

  const handleRemoveRule = (ruleType: string) => {
    // Find the rule being removed - we need its ID for backend deletion
    const ruleToRemove = alertRules.find((rule) => rule.ruleType === ruleType)
    if (petId != null && isRulesSaveRunning(petId) && !ruleToRemove?.id) {
      removedDuringSave.current.add(ruleType)
    }
    const updatedRules = alertRules.filter((rule) => rule.ruleType !== ruleType)
    setAlertRules(updatedRules)
    markEdited()

    // Update calculated glucose range preview
    setCalculatedGlucoseRange(calculateGlucoseRangePreview(updatedRules))

    // Validate the updated rules to clear validation errors for the removed rule
    validateAllRules(updatedRules)

    // If the rule has an ID (exists in backend), delete it from the backend
    if (ruleToRemove?.id) {
      trackRulesWrite(petId!, deleteRuleOnServer(ruleToRemove.id))
        .then(() => {
          console.log(`Deleted alert rule ${ruleToRemove.id} from backend`)
        })
        .catch((error: any) => {
          console.error(`Failed to delete alert rule ${ruleToRemove.id}:`, error)
          // The row was removed OPTIMISTICALLY, before the request. The delete
          // failed, so the rule is still live on the server and the Go alert
          // engine keeps evaluating it — and this list is fetched exactly once,
          // on mount, so nothing repairs the mismatch for the rest of the
          // session. Leaving it removed tells a vet that hypo alerting is off
          // for this patient when it is not, which is the wrong direction to be
          // wrong in. Put the rule back and say so.
          // `alertRules` here is the PRE-removal snapshot, which restores the
          // original order too; the exposure is an edit made to another rule
          // during the single in-flight request, which this file's other
          // handlers share and which the ~2s window makes remote.
          // Re-expressed in the CURRENT unit: the app-bar unit may have switched
          // while the delete was in flight, and the unit effect does not re-run.
          const restored = convertRulesToUnit(alertRules, unitRef.current)
          setAlertRules(restored)
          setCalculatedGlucoseRange(calculateGlucoseRangePreview(restored))
          validateAllRules(restored)
          notify('pos.alert_rules.delete_failed', { type: 'error' })
        })
    }
  }

  const getRuleByType = (ruleType: string): AlertRule | undefined => {
    return alertRules.find((rule) => rule.ruleType === ruleType)
  }

  // When the app-bar unit changes while this editor is mounted, re-express the
  // in-memory thresholds in the new unit so the numbers keep matching their
  // labels. This is a display re-expression (values are stored paired with
  // thresholdUnit), so it does not by itself mark the form dirty.
  useEffect(() => {
    setAlertRules((prev) => {
      // Per rule: a restored card (a failed remove) can be in another unit.
      const next = convertRulesToUnit(prev, globalUnit)
      return next.every((r, i) => r === prev[i]) ? prev : next
    })
  }, [globalUnit])

  // #36: every save -- the debounce, the Save button, the flush on unmount --
  // goes through THIS PET's queue: at most one per pet, and it survives this
  // editor unmounting. The job runs the latest runSave (via the ref).
  // `force`: the Save button -- retry even if nothing changed since the last
  // (failed) attempt. Automatic saves never resend unchanged values.
  const handleSaveRules = (force = false) => {
    if (petId == null) return
    if (force) snapRev.current = -1
    enqueueRulesSave(petId, () => runSaveRef.current())
  }

  const runSave = async () => {
    // Snapshot when the job STARTS (not when it was queued): a pending job
    // therefore saves the latest rules, even after this editor unmounted.
    const rulesToSave = rulesRef.current
    const startRev = editRev.current
    // Nothing new since the last save. A save that FAILED is not re-sent by
    // itself -- not even when the vet leaves (#36 review round 4: re-sending on
    // leave drew new defects three rounds running). Its failure is toasted
    // where it happened; the Save button retries.
    if (startRev === snapRev.current) return
    // X3: never from a view that does not reflect the server; never before a load
    if (loadFailedRef.current || !loadedRef.current) return
    const live = () => mountedRef.current
    let rulesToSend = rulesToSave
    if (live()) {
      if (!validateAllRules(rulesToSave)) {
        // Invalid: nothing to send (the errors are on screen). The queue moves on.
        notify(translate('pos.alert_rules.validation.cannot_save_with_errors'), { type: 'error' })
        return
      }
    } else {
      // A left-behind save (the editor is gone): save the rules that ARE valid
      // and say which patient kept unsaved changes -- one invalid rule used to
      // drop every edit in it with an anonymous toast (#36 review).
      rulesToSend = rulesToSave.filter((r) => validateAllRules([r], true))
      if (rulesToSend.length < rulesToSave.length) {
        notify(
          translate('pos.alert_rules.unsaved_invalid', {
            name: record?.name ?? `#${petId}`,
            _: `Some alert thresholds for ${record?.name ?? `#${petId}`} were invalid and were not saved.`,
          }),
          { type: 'warning' },
        )
      }
      if (rulesToSend.length === 0) return
    }
    snapRev.current = startRev
    removedDuringSave.current.clear()
    if (live()) setAutoSaving(true)
    try {
      const endpoint = 'alert-rules'
      let success = false
      const savedRules: AlertRule[] = []

      // Persist thresholds canonically in mmol/L — that is the unit the Go alert
      // engine and the rest of the backend compute in. The editor only edits in
      // the user's display unit; we convert to mmol/L here at the persistence
      // boundary (and back to the display unit after save, below).
      const canonicalRules = convertRulesToUnit(rulesToSend, 'mmol/L')

      // Use update for existing rules, create for new ones
      let skipped = 0
      for (const rule of canonicalRules) {
        // Removed from the editor while this save was running: never (re)send
        // it -- an update would resurrect it, a create would plant it.
        if (!rulesRef.current.some((r) => sameRule(r, rule))) {
          skipped++
          continue
        }
        // Prepare rule data outside try block so it's accessible in catch block
        const ruleData = {
          name: getRuleTypeName(rule.ruleType), // Required by backend
          pet: typeof rule.pet === 'object' ? rule.pet.id : rule.pet,
          ruleType: rule.ruleType,
          isActive: rule.isActive,
          thresholdUnit: rule.thresholdUnit,
          emailNotificationEnabled: rule.emailNotificationEnabled,
          smsNotificationEnabled: rule.smsNotificationEnabled,
          notes: rule.notes,

          // Add createdBy only for new rules (backend will auto-set from auth user)
          // Do NOT send createdBy for updates as it has update:false access control
          ...(!rule.id && identity?.id && { createdBy: identity.id }),

          // Conditional threshold fields based on rule type
          ...(rule.criticalThreshold !== undefined && {
            criticalThreshold: rule.criticalThreshold,
          }),
          ...(rule.warningThreshold !== undefined && { warningThreshold: rule.warningThreshold }),
          ...(rule.hyperglycemiaThreshold !== undefined && {
            hyperglycemiaThreshold: rule.hyperglycemiaThreshold,
          }),
          ...(rule.severeHyperglycemiaThreshold !== undefined && {
            severeHyperglycemiaThreshold: rule.severeHyperglycemiaThreshold,
          }),
          ...(rule.somogyiReboundThreshold !== undefined && {
            somogyiReboundThreshold: rule.somogyiReboundThreshold,
          }),
          ...(rule.dawnElevationThreshold !== undefined && {
            dawnElevationThreshold: rule.dawnElevationThreshold,
          }),
          ...(rule.rapidDropThreshold !== undefined && {
            rapidDropThreshold: rule.rapidDropThreshold,
          }),
          ...(rule.postprandialSpikeThreshold !== undefined && {
            postprandialSpikeThreshold: rule.postprandialSpikeThreshold,
          }),
          ...(rule.severeSpikeThreshold !== undefined && {
            severeSpikeThreshold: rule.severeSpikeThreshold,
          }),
          ...(rule.postprandialWindowHours !== undefined && {
            postprandialWindowHours: rule.postprandialWindowHours,
          }),
          ...(rule.preMealWindowHours !== undefined && {
            preMealWindowHours: rule.preMealWindowHours,
          }),
          ...(rule.patternAnalysisWindow !== undefined && {
            patternAnalysisWindow: rule.patternAnalysisWindow,
          }),
        }

        try {
          let result
          if (rule.id) {
            // Update existing rule
            result = await dataProvider.update(endpoint, {
              id: rule.id,
              data: ruleData,
              previousData: rule,
            })
          } else {
            // Create new rule
            result = await dataProvider.create(endpoint, { data: ruleData })
          }

          savedRules.push({
            ...rule,
            id: result.data.id || rule.id,
            createdAt: result.data.createdAt || rule.createdAt,
            updatedAt: result.data.updatedAt,
          })
        } catch (error: any) {
          console.error(`Failed to save rule ${rule.ruleType}:`, error)

          // Extract detailed error message
          let errorMessage = 'Unknown error'
          if (error?.body?.errors) {
            errorMessage = error.body.errors.map((e: any) => e.message).join(', ')
          } else if (error?.message) {
            errorMessage = error.message
          } else if (typeof error === 'string') {
            errorMessage = error
          }

          console.error(`Detailed error for ${rule.ruleType}:`, errorMessage)

          // Check if error is due to duplicate unique constraint
          if (errorMessage.includes('unique') || errorMessage.includes('duplicate')) {
            console.log(
              `Unique constraint error for ${rule.ruleType}, attempting to fetch and update existing rule`,
            )

            // Try to fetch the existing rule by pet + ruleType and update it
            try {
              const existingRules = await dataProvider.getList(endpoint, {
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'ASC' },
                filter: { pet: petId, ruleType: rule.ruleType },
              })

              if (existingRules.data && existingRules.data.length > 0) {
                const existingRule = existingRules.data[0]
                console.log(`Found existing rule with ID ${existingRule.id}, updating it`)

                // Update the existing rule
                const updateResult = await dataProvider.update(endpoint, {
                  id: existingRule.id,
                  data: ruleData,
                  previousData: existingRule,
                })

                savedRules.push({
                  ...rule,
                  id: updateResult.data.id,
                  createdAt: updateResult.data.createdAt,
                  updatedAt: updateResult.data.updatedAt,
                })

                console.log(`Successfully updated existing rule ${rule.ruleType}`)
              } else {
                notify('pos.alert_rules.toast.update_not_found', {
                  type: 'error',
                  messageArgs: { rule: getRuleTypeLabel(rule.ruleType, translate) },
                })
              }
            } catch (updateError) {
              console.error(
                `Failed to fetch and update existing rule ${rule.ruleType}:`,
                updateError,
              )
              notify('pos.alert_rules.toast.update_failed', {
                type: 'error',
                messageArgs: { rule: getRuleTypeLabel(rule.ruleType, translate) },
              })
            }
          } else if (
            errorMessage.includes('not allowed') ||
            errorMessage.includes('permission') ||
            errorMessage.includes('unauthorized')
          ) {
            notify('pos.alert_rules.toast.permission_denied', { type: 'error' })
            // Stop processing — a permission failure applies to every rule, so
            // continuing would only repeat this toast. BREAK, not throw: the
            // post-loop block must still run to persist the ids of rules that
            // did save before this one.
            break
          } else {
            // Show the actual error to the user
            notify('pos.alert_rules.toast.save_rule_failed', {
              type: 'error',
              messageArgs: {
                rule: getRuleTypeLabel(rule.ruleType, translate),
                error: errorMessage,
              },
            })
          }
        }
      }

      // Merge the server ids of the rules that saved into the CURRENT rules --
      // never replace them with this save's snapshot. The snapshot is older
      // than the screen whenever the vet edited mid-save (the edit vanished and
      // was never sent), it is in the unit of the save's closure (after an
      // app-bar switch mid-save the numbers no longer matched their labels),
      // and it lacks every rule that FAILED (a partial save dropped that card
      // while the server kept evaluating the rule). The same array is kept when
      // no id is new, so a rule that keeps failing does not re-arm the
      // debounce into a retry loop.
      const idByType = new Map(savedRules.map((r) => [r.ruleType, r]))
      const sentIdByType = new Map(canonicalRules.map((r) => [r.ruleType, r.id]))
      const mergeIds = (current: AlertRule[]) => {
        let changed = false
        const next = current.map((r) => {
          const saved = idByType.get(r.ruleType)
          if (!saved || saved.id === r.id) return r
          // Not the rule this save sent (removed + re-added mid-save): leave it.
          if (!sameRule(r, { ruleType: r.ruleType, id: sentIdByType.get(r.ruleType) })) return r
          changed = true
          return { ...r, id: saved.id, createdAt: saved.createdAt ?? r.createdAt }
        })
        return changed ? next : current
      }

      // A rule this save CREATED whose card the vet removed meanwhile: the
      // remove had no id to delete, so the server now evaluates a rule nobody
      // sees. Delete it -- unless the vet re-added that type on this pet, in
      // which case the new card takes the id (merge below). Runs even after a
      // pet switch: the id is this pet's own row.
      for (const saved of savedRules) {
        const wasNew = !canonicalRules.find((r) => r.ruleType === saved.ruleType)?.id
        const readded = rulesRef.current.some((r) => sameRule(r, { ruleType: saved.ruleType }))
        if (wasNew && saved.id && removedDuringSave.current.has(saved.ruleType) && !readded) {
          trackRulesWrite(petId!, deleteRuleOnServer(saved.id)).catch(() => {
            // Still live on the server: show it again, like a failed remove.
            if (live()) {
              setAlertRules((current) =>
                current.some((r) => r.ruleType === saved.ruleType)
                  ? current
                  : [...current, ...convertRulesToUnit([saved], unitRef.current)],
              )
            }
            notify('pos.alert_rules.delete_failed', { type: 'error' })
          })
        }
      }

      // Check if all rules were saved successfully
      const totalRules = canonicalRules.length - skipped
      const savedCount = savedRules.length

      // The editor's rules learn the new ids even after it unmounted, so a later
      // queued save of the same editor updates the rows instead of re-creating.
      rulesRef.current = mergeIds(rulesRef.current)
      if (!live()) {
        // The editor is gone: this toast is the only trace of the lost edits.
        if (savedCount < totalRules) {
          notify(
            translate('pos.alert_rules.unsaved_failed', {
              name: record?.name ?? `#${petId}`,
              _: `Some alert thresholds for ${record?.name ?? `#${petId}`} could not be saved.`,
            }),
            { type: 'error' },
          )
        }
        return
      }
      setAlertRules(mergeIds)
      const editedMeanwhile = editRev.current !== startRev

      if (savedCount === totalRules) {
        // All rules saved successfully
        notify('pos.alert_rules.notifications.saved_successfully', {
          type: 'success',
          messageArgs: { smart_count: savedCount },
        })
        // Still dirty if the vet edited after this save took its snapshot.
        setHasChanges(editedMeanwhile)
        success = true
      } else if (savedCount > 0) {
        // Partial success (ids already merged above)
        notify('pos.alert_rules.toast.saved_partial', {
          type: 'warning',
          messageArgs: { saved: savedCount, total: totalRules },
        })
        setHasChanges(true) // Keep changes flag: the Save button retries (force)
      } else {
        // Complete failure
        notify('pos.alert_rules.toast.save_failed_details', { type: 'error' })
      }

      // Don't fallback to localStorage if we had API errors
      // The localStorage fallback should only be used for true offline scenarios
      // If we reach here with success=false, it means all API calls failed
      // Those failures were already reported to the user above
    } catch (error) {
      console.error('Error saving alert rules:', error)
      notify('pos.alert_rules.toast.save_failed', { type: 'error' })
    } finally {
      if (live()) setAutoSaving(false)
    }
  }
  const runSaveRef = useRef(runSave)
  runSaveRef.current = runSave

  // #36/X5: leaving the pet (or PetEdit) with unsaved edits used to cancel the
  // pending autosave and drop them. Flush them through the pet's queue instead;
  // the next editor of this pet waits for it before loading.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount/unmount only -- petId is fixed per instance (the editor is keyed by pet) and the rest is read through refs
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (hasChangesRef.current && loadedRef.current && !loadFailedRef.current && petId != null) {
        enqueueRulesSave(petId, () => runSaveRef.current())
      }
    }
  }, [])

  // Auto-save alert rules when they change (debounced)
  useEffect(() => {
    if (!hasChanges || !petId) return

    const timeoutId = setTimeout(() => {
      handleSaveRules()
    }, 2000) // 2 second debounce

    return () => clearTimeout(timeoutId)
  }, [alertRules, hasChanges, petId])

  // Vet clicked "copy thresholds" on the predecessor banner: replace the
  // default-seeded rules with the copied ones (de-duped by type) and mark the
  // editor dirty so the vet sees the values and they persist via the normal
  // save path. Hide the banner afterward by treating the pet as configured.
  const handleCopyFromPredecessor = (copied: CopiedRule[]) => {
    const byType = new Map<string, AlertRule>()
    for (const r of copied) byType.set(r.ruleType, { ...(r as unknown as AlertRule) })
    // Copied rules carry the predecessor's thresholdUnit; re-express them in the
    // app-wide display unit so they match the rest of the editor.
    const merged = convertRulesToUnit(Array.from(byType.values()), globalUnit)
    if (merged.length === 0) return
    setAlertRules(merged)
    setCalculatedGlucoseRange(calculateGlucoseRangePreview(merged))
    validateAllRules(merged)
    setHadSavedRules(true)
    markEdited()
  }

  if (!petId) {
    return null
  }

  return (
    // Full form width: SimpleForm's stack is align-items:flex-start, so the
    // card shrink-wrapped to its header's one-line width (526px on a 390
    // phone) instead of letting the header wrap.
    <Card sx={{ mt: 3, width: '100%' }}>
      <CardHeader
        // Phones: the Add rule / Save buttons (140px min each, nowrap) pushed
        // this card to ~600px inside a 324px form, off the clipped page. Below
        // sm they drop onto their own row under the title; sm+ unchanged.
        sx={{
          '@media (max-width:599.95px)': {
            flexWrap: 'wrap',
            '& .MuiCardHeader-action': { m: 0, mt: 1.5, width: '100%' },
          },
        }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <WarningIcon color="primary" />
            <Typography variant="h6">{translate('pos.alert_rules.title')}</Typography>
            {calculatedGlucoseRange && !loadFailed && (
              <Chip
                label={`${translate('pos.alert_rules.glucose_range')}: ${
                  globalUnit === 'mg/dL'
                    ? `${calculatedGlucoseRange
                        .split(' - ')
                        .map((v) => mmolToMgdl(parseFloat(v)).toString())
                        .join(' - ')} mg/dL`
                    : `${calculatedGlucoseRange} mmol/L`
                }`}
                color="primary"
                size="small"
                variant="outlined"
              />
            )}
            {autoSaving && (
              <Chip
                label={translate('pos.alert_rules.auto_saving')}
                color="info"
                size="small"
                icon={<SaveIcon />}
              />
            )}
          </Box>
        }
        subheader={translate('pos.alert_rules.configuration_desc')}
        action={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
            {/* Unit is chosen once, in the top app bar; thresholds here follow it. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => setAddRuleMenuAnchor(e.currentTarget)}
              disabled={loadFailed || loading}
              sx={{
                minWidth: 140,
                height: '40px',
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              {translate('pos.alert_rules.add_rule')}
            </Button>
            <Menu
              anchorEl={addRuleMenuAnchor}
              open={Boolean(addRuleMenuAnchor)}
              onClose={() => setAddRuleMenuAnchor(null)}
              PaperProps={{
                sx: { maxHeight: 400 },
              }}
            >
              {[...ruleTypeDefinitions.basic, ...ruleTypeDefinitions.advanced]
                .filter((ruleType) => !getRuleByType(ruleType))
                .map((ruleType) => (
                  <MenuItem
                    key={ruleType}
                    onClick={() => {
                      handleAddRule(ruleType)
                      setAddRuleMenuAnchor(null)
                    }}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {getRuleTypeLabel(ruleType, translate)}
                  </MenuItem>
                ))}
              {[...ruleTypeDefinitions.basic, ...ruleTypeDefinitions.advanced].filter(
                (ruleType) => !getRuleByType(ruleType),
              ).length === 0 && (
                <MenuItem disabled sx={{ fontSize: '0.875rem' }}>
                  <em>{translate('pos.alert_rules.all_rule_types_added')}</em>
                </MenuItem>
              )}
            </Menu>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={() => handleSaveRules(true)}
              disabled={
                !hasChanges || autoSaving || loadFailed || Object.keys(validationErrors).length > 0
              }
              sx={{
                minWidth: 120,
                height: '40px',
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              {autoSaving
                ? translate('pos.alert_rules.auto_saving')
                : translate('pos.alert_rules.save')}
            </Button>
          </Box>
        }
      />

      <CardContent>
        {loading ? (
          <Typography>
            {translate(
              waitingForQueue ? 'pos.alert_rules.saving_previous' : 'pos.alert_rules.loading',
            )}
          </Typography>
        ) : loadFailed ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
                {translate('pos.alert_rules.retry')}
              </Button>
            }
          >
            {translate('pos.alert_rules.load_failed')}
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {translate('pos.alert_rules.configuration_info')}
              </Typography>
            </Alert>

            <AlertThresholdCopyBanner
              petId={petId}
              currentHasSavedRules={hadSavedRules}
              onCopy={handleCopyFromPredecessor}
            />

            {alertRules.map((rule, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  {/* Wraps on phones: the Active / Email switches ran past a
                      390px screen beside the rule name. */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      columnGap: 2,
                      rowGap: 1,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {getRuleTypeIcon(rule.ruleType)}
                      <Typography variant="h6">
                        {getRuleTypeLabel(rule.ruleType, translate)}
                      </Typography>
                      {/* Rule on/off is configuration, not clinical priority:
                          quiet positive / neutral (was filled MUI success). */}
                      <ToneChip
                        label={
                          rule.isActive
                            ? translate('pos.alert_rules.fields.active_status')
                            : translate('pos.alert_rules.fields.inactive_status')
                        }
                        tone={rule.isActive ? 'positive' : 'neutral'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={rule.isActive}
                            onChange={(e) => handleRuleChange(index, 'isActive', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={translate('pos.alert_rules.fields.active')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={rule.emailNotificationEnabled || false}
                            onChange={(e) =>
                              handleRuleChange(index, 'emailNotificationEnabled', e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        }
                        label={translate('pos.alert_rules.fields.email_notifications')}
                      />
                      {alertRules.length > 1 && (
                        <Tooltip title={translate('pos.alert_rules.delete_rule_tooltip')}>
                          <IconButton
                            onClick={() => handleRemoveRule(rule.ruleType)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    {/* Dynamic threshold fields based on rule type */}
                    {(() => {
                      const visibility = getFieldVisibility(rule.ruleType)
                      return (
                        <>
                          {/* Trigger Count - compact display */}
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label={translate('pos.alert_rules.fields.trigger_count')}
                              type="number"
                              value={rule.triggerCount || 0}
                              disabled
                              size="small"
                              helperText={translate('pos.alert_rules.fields.trigger_count_helper')}
                            />
                          </Grid>

                          {visibility.criticalThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.critical_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.criticalThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'criticalThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '50' : '2.8'}
                                inputProps={getInputPropsForField('criticalThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]?.criticalThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.criticalThreshold ||
                                  convertRangeText(
                                    translate('pos.alert_rules.help_ranges.critical_threshold'),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.warningThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.warning_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.warningThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'warningThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '72' : '4.0'}
                                inputProps={getInputPropsForField('warningThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]?.warningThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]?.warningThreshold ||
                                  convertRangeText(
                                    translate('pos.alert_rules.help_ranges.warning_threshold'),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.hyperglycemiaThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.hyperglycemia_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.hyperglycemiaThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'hyperglycemiaThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '216' : '12.0'}
                                inputProps={getInputPropsForField('hyperglycemiaThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.hyperglycemiaThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.hyperglycemiaThreshold ||
                                  convertRangeText(
                                    translate(
                                      'pos.alert_rules.help_ranges.hyperglycemia_threshold',
                                    ),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.severeHyperglycemiaThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.severe_hyperglycemia_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.severeHyperglycemiaThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'severeHyperglycemiaThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '288' : '16.0'}
                                inputProps={getInputPropsForField('severeHyperglycemiaThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.severeHyperglycemiaThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.severeHyperglycemiaThreshold ||
                                  convertRangeText(
                                    translate(
                                      'pos.alert_rules.help_ranges.severe_hyperglycemia_threshold',
                                    ),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.somogyiReboundThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.somogyi_rebound_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.somogyiReboundThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'somogyiReboundThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '180' : '10.0'}
                                inputProps={getInputPropsForField('somogyiReboundThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.somogyiReboundThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.somogyiReboundThreshold ||
                                  convertRangeText(
                                    translate(
                                      'pos.alert_rules.help_ranges.somogyi_rebound_threshold',
                                    ),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.dawnElevationThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.dawn_elevation_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.dawnElevationThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'dawnElevationThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '162' : '9.0'}
                                inputProps={getInputPropsForField('dawnElevationThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.dawnElevationThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.dawnElevationThreshold ||
                                  convertRangeText(
                                    translate(
                                      'pos.alert_rules.help_ranges.dawn_elevation_threshold',
                                    ),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.rapidDropThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.drop_threshold')} (${translate('pos.alert_rules.per_15min', { unit: globalUnit })})`}
                                type="number"
                                value={rule.rapidDropThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'rapidDropThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '36' : '2.0'}
                                inputProps={getInputPropsForField('rapidDropThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.rapidDropThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.rapidDropThreshold ||
                                  convertRangeText(
                                    translate('pos.alert_rules.help_ranges.rapid_drop_threshold'),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.patternAnalysisWindow && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={translate('pos.alert_rules.thresholds.analysis_window')}
                                type="number"
                                value={rule.patternAnalysisWindow || 24}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'patternAnalysisWindow',
                                    parseInt(e.target.value, 10),
                                  )
                                }
                                placeholder="24"
                                inputProps={getInputPropsForField('patternAnalysisWindow')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.patternAnalysisWindow
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.patternAnalysisWindow ||
                                  translate('pos.alert_rules.help_ranges.analysis_window')
                                }
                              />
                            </Grid>
                          )}

                          {visibility.postprandialSpikeThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.postprandial_spike_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.postprandialSpikeThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'postprandialSpikeThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '108' : '6.0'}
                                inputProps={getInputPropsForField('postprandialSpikeThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.postprandialSpikeThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.postprandialSpikeThreshold ||
                                  convertRangeText(
                                    translate(
                                      'pos.alert_rules.help_ranges.postprandial_spike_threshold',
                                    ),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.severeSpikeThreshold && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={`${translate('pos.alert_rules.thresholds.severe_spike_threshold')} (${globalUnit})`}
                                type="number"
                                value={rule.severeSpikeThreshold || ''}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'severeSpikeThreshold',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder={globalUnit === 'mg/dL' ? '162' : '9.0'}
                                inputProps={getInputPropsForField('severeSpikeThreshold')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.severeSpikeThreshold
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.severeSpikeThreshold ||
                                  convertRangeText(
                                    translate('pos.alert_rules.help_ranges.severe_spike_threshold'),
                                    globalUnit,
                                  )
                                }
                              />
                            </Grid>
                          )}

                          {visibility.postprandialWindowHours && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={translate(
                                  'pos.alert_rules.thresholds.postprandial_window_hours',
                                )}
                                type="number"
                                value={rule.postprandialWindowHours || 4}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'postprandialWindowHours',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder="4.0"
                                inputProps={getInputPropsForField('postprandialWindowHours')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.postprandialWindowHours
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.postprandialWindowHours ||
                                  translate('pos.alert_rules.help_ranges.postprandial_window_hours')
                                }
                              />
                            </Grid>
                          )}

                          {visibility.preMealWindowHours && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={translate(
                                  'pos.alert_rules.thresholds.pre_meal_window_hours',
                                )}
                                type="number"
                                value={rule.preMealWindowHours || 1}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'preMealWindowHours',
                                    parseFloat(e.target.value),
                                  )
                                }
                                placeholder="1.0"
                                inputProps={getInputPropsForField('preMealWindowHours')}
                                size="small"
                                error={
                                  !!validationErrors[`${rule.ruleType}-${index}`]
                                    ?.preMealWindowHours
                                }
                                helperText={
                                  validationErrors[`${rule.ruleType}-${index}`]
                                    ?.preMealWindowHours ||
                                  translate('pos.alert_rules.help_ranges.pre_meal_window_hours')
                                }
                              />
                            </Grid>
                          )}
                        </>
                      )
                    })()}

                    {/* Notes field - compact layout */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={translate('pos.alert_rules.fields.notes')}
                        value={rule.notes || ''}
                        onChange={(e) => handleRuleChange(index, 'notes', e.target.value)}
                        placeholder={translate('pos.alert_rules.fields.notes_placeholder')}
                        multiline
                        rows={1}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>

                  {/* Rule-specific help text - compact */}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {rule.ruleType === 'critical_hypoglycemia' &&
                        translate('pos.alert_rules.help_text.critical_hypoglycemia')}
                      {rule.ruleType === 'warning_hypoglycemia' &&
                        translate('pos.alert_rules.help_text.warning_hypoglycemia')}
                      {rule.ruleType === 'critical_hyperglycemia' &&
                        translate('pos.alert_rules.help_text.critical_hyperglycemia')}
                      {rule.ruleType === 'warning_hyperglycemia' &&
                        translate('pos.alert_rules.help_text.warning_hyperglycemia')}
                      {rule.ruleType === 'rapid_drop' &&
                        translate('pos.alert_rules.help_text.rapid_drop')}
                      {rule.ruleType === 'dawn_pattern' &&
                        translate('pos.alert_rules.help_text.dawn_pattern')}
                      {rule.ruleType === 'somogyi_pattern' &&
                        translate('pos.alert_rules.help_text.somogyi_pattern')}
                      {rule.ruleType === 'postprandial_spikes' &&
                        translate('pos.alert_rules.help_text.postprandial_spikes')}
                    </Typography>

                    {/* Special note for Somogyi pattern - more compact */}
                    {rule.ruleType === 'somogyi_pattern' && (
                      <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                        <Typography variant="caption">
                          {translate('pos.alert_rules.somogyi_note')}
                        </Typography>
                      </Alert>
                    )}

                    {/* Metadata display - inline */}
                    {(rule.lastTriggered || rule.triggerCount > 0) && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {rule.lastTriggered &&
                          `${translate('pos.alert_rules.metadata.last_triggered')}: ${new Date(rule.lastTriggered).toLocaleString()} • `}
                        {rule.triggerCount > 0 &&
                          `${translate('pos.alert_rules.metadata.total_triggers')}: ${rule.triggerCount}`}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}

            {alertRules.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {translate('pos.alert_rules.no_rules_configured')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Keyed by PET (vic/vetpo#36; TLA+ docs/spec/tla/AlertRulesCoordinator.tla).
 * PetEdit keeps this subtree mounted across a /pets/A -> /pets/B route change,
 * so ONE editor instance used to carry its refs, dirty flag and in-flight save
 * from one pet to the next -- the source of A5-A11 and X1-X6. With the key,
 * every pet switch unmounts the old editor (its unsaved edits are flushed
 * through alertRulesSaveQueue) and mounts a fresh one that waits for that
 * pet's queue before loading.
 */
export const AlertRulesSection: React.FC = () => {
  const record = useRecordContext()
  return <AlertRulesEditor key={record?.id != null ? String(record.id) : 'no-pet'} />
}

export default AlertRulesSection
